import {
  ProcessNode,
  ProcessKind,
  EmissionsVariable,
  OutputVariable,
  InputVariable,
  LocalVariable,
} from './emissionsModelSchema';

const TEMPLATE_INPUTS: Record<string, InputVariable> = {
  kg_in: {
    displayName: 'Input mass',
    name: 'kg_in',
    unit: 'kilogram',
  },
  kwh_used: {
    displayName: 'Kilowatt-hour used',
    name: 'kwh_used',
    unit: 'kilowatt-hour',
  },
  mmbtu_used: {
    name: 'mmbtu_used',
    displayName: 'Natural gas used',
    unit: 'mmbtu',
  },
  item_count: {
    name: 'item_count',
    displayName: 'Item count',
    unit: 'order',
  },
};

const TEMPLATE_OUTPUTS: Record<string, OutputVariable> = {
  kg_out: {
    displayName: 'Output mass',
    name: 'kg_out',
    unit: 'kilogram',
  },
};

const TEMPLATE_EMISSIONS: Record<string, EmissionsVariable> = {
  emissions: {
    displayName: 'Emissions',
    name: 'emissions',
    unit: 'kgco2e',
    emissionsKind: 'emissions',
    emissionsFactor: '',
    emissionsFactorUnit: '',
    activityExpression: '',
  },
};

const TEMPLATE_LOCALS: Record<string, LocalVariable> = {
  kg_from_items: {
    name: 'kg',
    displayName: 'Total mass',
    unit: 'kilogram',
    expression: 'item_count * kg_per_item',
  },
};

export type ProcessNodeVariables = Pick<
  ProcessNode,
  'inputs' | 'outputs' | 'emissions' | 'results' | 'locals'
>;

/* CHANGELOG:
 *  - 2023-03-14: Initial sketch with processes from
 *    emstable_2114T6jcUscTvnQg6Ft9
 *  - 2023-03-15: Added `End of Life`, `Injection Molding`, `Use phase`
 */

export const DEFAULT_VARIABLES_FOR_PROCESS_KIND: Partial<
  Record<ProcessKind, ProcessNodeVariables>
> = {
  'Cut and sew': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out / (1 - loss_rate_for_cut_and_sew)',
      },
      {
        ...TEMPLATE_INPUTS.kwh_used,
        expression: 'kg_out * kwh_per_kg_for_cut_and_sew',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [
      {
        ...TEMPLATE_EMISSIONS.emissions,
        emissionsKind: 'emissions',
        emissionsFactor: 'kgco2e_per_kg_for_cut_and_sew',
        emissionsFactorUnit: 'kilogram[co2e]/kilogram',
        activityExpression: 'kg_out',
        activityUnit: 'kilogram',
      },
    ],
  },
  'Electricity production': {
    outputs: [
      // Commonly paired with a downstream node that has `kwh_used` as an input.
      TEMPLATE_INPUTS.kwh_used,
    ],
    emissions: [TEMPLATE_EMISSIONS.emissions],
    results: [],
    locals: [
      {
        name: 'kwh_produced',
        displayName: 'kwh produced',
        unit: 'kilowatt-hour',
        expression: 'kwh_used / (1 - electricity_loss_rate)',
      },
    ],
  },
  'End of life': {
    inputs: [TEMPLATE_INPUTS.item_count],
    emissions: [TEMPLATE_EMISSIONS.emissions],
    locals: [TEMPLATE_LOCALS.kg_from_items],
  },
  'Fabric coloration': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out / (1 - loss_rate_for_fabric_coloration)',
      },
      TEMPLATE_INPUTS.kwh_used,
      {
        displayName: 'Thermal output',
        name: 'thermal_output',
        unit: 'mmbtu',
        expression: 'kg_out * mmbtu_per_kg_for_fabric_coloration',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [
      {
        ...TEMPLATE_EMISSIONS.emissions,
        emissionsKind: 'emissions',
        emissionsFactor: 'kgco2e_per_kg_for_fabric_coloration',
        emissionsFactorUnit: 'kilogram[co2e]/kilogram',
        activityExpression: 'kg_out',
        activityUnit: 'kilogram',
      },
    ],
  },
  'Fabric preparation': {
    inputs: [TEMPLATE_INPUTS.kg_in, TEMPLATE_INPUTS.kwh_used],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [TEMPLATE_EMISSIONS.emissions],
  },
  'Injection Molding': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out / (1 - loss_rate_for_injection_molding)',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [TEMPLATE_EMISSIONS.emissions],
  },
  'Raw material production': {
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [TEMPLATE_EMISSIONS.emissions],
  },
  'Textile formation': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out / (1 - loss_rate_for_textile_formation)',
      },
      {
        ...TEMPLATE_INPUTS.kwh_used,
        expression: 'kwh_per_kg_for_textile_formation  * kg_out',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
  },
  'Thermal energy production': {
    outputs: [],
    emissions: [TEMPLATE_EMISSIONS.emissions],
  },
  'Transport between processes': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
    emissions: [TEMPLATE_EMISSIONS.emissions],
  },

  // From
  // https://admin.watershedclimate.com/emissions-models/emstable_2114RY8Wb4FTea4NwHCh
  // TODO (bryan): I feel like... this is too-specific to apparel? Maybe it
  // should just be item_count?
  'Use phase': {
    inputs: [TEMPLATE_INPUTS.item_count],
    outputs: [TEMPLATE_INPUTS.kwh_used, TEMPLATE_INPUTS.mmbtu_used],
    locals: [
      TEMPLATE_LOCALS.kg_from_items,
      {
        name: 'lifetime_washes',
        displayName: 'Lifetime washes',
        expression: 'lifetime_uses / uses_per_wash',
      },
    ],
  },

  'Yarn formation': {
    inputs: [
      {
        ...TEMPLATE_INPUTS.kg_in,
        expression: 'kg_out / (1 - loss_rate_for_yarn_formation)',
      },
      {
        ...TEMPLATE_INPUTS.kwh_used,
        expression: 'kwh_per_kg_for_yarn_formation * kg_out',
      },
    ],
    outputs: [TEMPLATE_OUTPUTS.kg_out],
  },
};
